import { compact, get, isEmpty, orderBy } from 'lodash';
import {
  AssemblyJourneys,
  DefaultRotationValues,
} from 'src/constants/assemblyJourneys';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import {
  ComponentTypes,
  DynamicEvents,
  IAssemblyComponent,
  IAssemblyConfig,
  IComponent,
  IComponentType,
  IExpiredStatus,
  ILayout,
  IScreen,
  ISection,
  ISectionConfig,
  IStatus,
  SectionTypes,
} from 'src/interfaces';
import { isDisallowedOutdoorSection } from 'src/utils/assembly-utils';
import { getSectionEvents } from 'src/utils/section-types-helper';
import {
  getDaysFromExpiryMessage,
  getExpiryStatusMessage,
} from 'src/utils/util-functions';

import { getEarliestComponentExpiryDate } from '../listAssemblyPage/modules/assemblyTableRow/AssemblyTableRow.helper';

export const hasAssemblyComponent = (
  screen: IScreen,
  section: ISection,
  assemblyComponents: Partial<IAssemblyComponent>[],
) =>
  assemblyComponents.some(
    (c) =>
      c.layout_screen_id === screen.layoutScreenId &&
      c.screen_section_id === section.screenSectionId,
  );

export const checkNumOfComponents = (
  layouts: ILayout[],
  assemblyComponents: Partial<IAssemblyComponent>[],
) =>
  layouts.every((layout) =>
    layout.screens.every((screen) =>
      screen.sections.every(
        (section) =>
          isDisallowedOutdoorSection(layout, section) ||
          hasAssemblyComponent(screen, section, assemblyComponents),
      ),
    ),
  );

export const findMatchedAssemblyComponents = (
  assemblyComponents: IAssemblyComponent[],
  layoutScreenId?: number,
  screenSectionId?: number,
  sort: string[] = [],
  parentAssemblyComponents: IAssemblyComponent[] = [],
  order: Array<'asc' | 'desc'> = [],
) => {
  const matchedAssemblyComponents = assemblyComponents.filter(
    (item) =>
      item.layout_screen_id === layoutScreenId &&
      item.screen_section_id === screenSectionId,
  );
  const matchedParentAssemblyComponents = parentAssemblyComponents.filter(
    (item) =>
      item.layout_screen_id === layoutScreenId &&
      item.screen_section_id === screenSectionId,
  );

  return orderBy(
    compact(
      isEmpty(matchedAssemblyComponents)
        ? matchedParentAssemblyComponents
        : matchedAssemblyComponents,
    ),
    sort,
    order,
  );
};

export const showParentIcon = (
  assemblyComponents: Partial<IAssemblyComponent>[],
  layoutScreenId?: number,
  screenSectionId?: number,
  parentId?: number,
) => {
  const matchedAssemblyComponents = assemblyComponents.filter(
    (item) =>
      item.layout_screen_id === layoutScreenId &&
      item.screen_section_id === screenSectionId,
  );
  if (parentId !== null && matchedAssemblyComponents.length === 0) {
    return true;
  } else {
    return false;
  }
};

export const showChildIcon = (
  assemblyComponents: Partial<IAssemblyComponent>[],
  layoutScreenId?: number,
  screenSectionId?: number,
  parentId?: number,
) => {
  const matchedAssemblyComponents = assemblyComponents.filter(
    (item) =>
      item.layout_screen_id === layoutScreenId &&
      item.screen_section_id === screenSectionId,
  );
  if (parentId !== null && matchedAssemblyComponents.length > 0) {
    return true;
  } else {
    return false;
  }
};

export const handleFiltersForComponents = (
  currentDate: string | null,
  layoutTypeId: number,
  componentTypes: IComponentType[] = [],
  replaceComponentID?: number,
  selectedSection?: {
    layoutScreenId?: number;
    screenSectionId?: number;
    sectionId?: number;
    width: number;
    height: number;
    config?: ISectionConfig | undefined;
  },
  backupAssemblyComponent?: number[],
) => {
  const basicFilters = [
    ...(currentDate ? [`end_date.from=${currentDate}`] : []),
    `width=${selectedSection?.width}`,
    `height=${selectedSection?.height}`,
    `layout_type_id=${layoutTypeId}`,
  ];
  const idsToExclude: number[] = [];

  if (replaceComponentID) {
    idsToExclude.push(replaceComponentID);
  }
  if (backupAssemblyComponent?.length) {
    idsToExclude.push(...backupAssemblyComponent);
  }

  if (idsToExclude?.length) {
    basicFilters.push(`id.notlist=${idsToExclude.join('|')}`);
  }

  const suggestedComponentTypes = get(
    selectedSection,
    'config.suggestedComponentTypes',
    [],
  ) as ComponentTypes[];

  const componentTypeIDs = isEmpty(suggestedComponentTypes)
    ? componentTypes
        .filter(
          (item) =>
            item.name !== ComponentTypes.COD &&
            item.name !== ComponentTypes.COD_THANK_YOU,
        )
        .map((item) => item.id)
    : componentTypes
        .filter((item) =>
          suggestedComponentTypes.includes(item.name as ComponentTypes),
        )
        .map((item) => item.id);

  basicFilters.push(`component_type_id.list=${componentTypeIDs.join('|')}`);

  return basicFilters;
};

// TODO: the solution should be revised here
export const handleStyleWithScale = (style: string, scale?: number) => {
  const styleWithScale = style.split(';').map((styleItem) => {
    let newStyleItem = styleItem;
    const regex = /(\d+)px(?!\))/;
    const match = newStyleItem.match(regex);
    if (match && scale) {
      const matchVal = Number(match[1]) * scale;

      newStyleItem = newStyleItem.replace(regex, `${matchVal}px !important`);
    }
    const regexWithParentheses = /(\d+)px\)/;
    const matchWithParentheses = newStyleItem.match(regexWithParentheses);
    if (matchWithParentheses && scale) {
      const matchVal = Number(matchWithParentheses[1]) * scale;

      newStyleItem = newStyleItem.replace(
        regexWithParentheses,
        `${matchVal}px) !important;`,
      );
    }

    return newStyleItem;
  });

  return styleWithScale.join(';');
};

export const handleEventStyle = (
  activeAssemblyJourney?: AssemblyJourneys,
  events?: DynamicEvents,
  scale?: number,
  sectionTypes?: SectionTypes[],
) => {
  if (!events || !activeAssemblyJourney) return;

  const newEvents = getSectionEvents(activeAssemblyJourney, sectionTypes);

  for (const key in events) {
    if (newEvents.some((event) => key === event)) {
      return handleStyleWithScale(events[key].style, scale);
    }
  }

  return;
};

export const getZIndexStylesFromSectionStyle = (style?: string) => {
  if (!style) return 1;

  const zIndexStyles = style
    .split(';')
    .filter((style) => style.includes('z-index:'));
  const match = zIndexStyles[zIndexStyles.length - 1]?.match(/\d+/);

  if (match) {
    return Number(match[0]);
  }

  return 1;
};

export const getDuration = (
  layout: any,
  type: 'defaultLayout' | 'sectionRotation' | 'takeover',
  fallback: number,
) => layout?.[type]?.duration ?? fallback;

export const getConfigWithDefaultValue = (
  layout: any,
  hasSectionRotation?: boolean,
  hasTakeOver?: boolean,
) => ({
  layout: {
    defaultLayout: {
      duration: getDuration(
        layout,
        'defaultLayout',
        DefaultRotationValues.DEFAULT,
      ),
    },
    ...(hasSectionRotation && {
      sectionRotation: {
        duration: getDuration(
          layout,
          'sectionRotation',
          DefaultRotationValues.SECTION_TIMER,
        ),
      },
    }),
    ...(hasTakeOver && {
      takeover: {
        duration: getDuration(
          layout,
          'takeover',
          DefaultRotationValues.LAYOUT_TIMER,
        ),
      },
    }),
  },
});

export const getConfigWithParentValue = (
  layout: any,
  parentAssemblyConfig?: IAssemblyConfig,
  hasSectionRotation?: boolean,
  hasTakeOver?: boolean,
) => {
  if (!layout) {
    return undefined;
  }

  return {
    layout: {
      defaultLayout: {
        duration:
          layout.defaultLayout?.duration ??
          parentAssemblyConfig?.layout?.defaultLayout?.duration,
      },
      ...(hasSectionRotation && {
        sectionRotation: {
          duration:
            layout.sectionRotation?.duration ??
            parentAssemblyConfig?.layout?.sectionRotation?.duration,
        },
      }),
      ...(hasTakeOver && {
        takeover: {
          duration:
            layout.takeover?.duration ??
            parentAssemblyConfig?.layout?.takeover?.duration,
        },
      }),
    },
  };
};

export const getUpdatedAssemblyValues = (
  status: IStatus,
  assemblyConfig?: IAssemblyConfig,
  assemblyName?: string,
  variation_parent_id?: number | null,
  parentAssemblyConfig?: IAssemblyConfig,
  hasSectionRotation?: boolean,
  hasTakeOver?: boolean,
) => {
  const { layout } = assemblyConfig || {};

  const config =
    hasSectionRotation || hasTakeOver
      ? variation_parent_id
        ? getConfigWithParentValue(
            layout,
            parentAssemblyConfig,
            hasSectionRotation,
            hasTakeOver,
          )
        : getConfigWithDefaultValue(layout, hasSectionRotation, hasTakeOver)
      : undefined;

  return {
    name: assemblyName?.trim() ?? '',
    status,
    ...(config !== undefined && { config }),
    narrative: (
      narrativeGenerators[COLLECTIONS.ASSEMBLIES][USER_ACTIONS.UPDATE] as (
        assemblyName: string,
      ) => string
    )(assemblyName?.trim() ?? ''),
  };
};

/**
 * Derives the expiry state used by the save toast and the save button from the
 * given components. Callers must pass the current form components so the result
 * reflects add/replace changes rather than a stale snapshot.
 */
export const getAssemblyExpiryInfo = (components: IComponent[]) => {
  const expiryMessage = getExpiryStatusMessage(
    getEarliestComponentExpiryDate(components),
  );

  return {
    isExpired: expiryMessage === IExpiredStatus.expired,
    expiringInDays: getDaysFromExpiryMessage(expiryMessage),
  };
};
